import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { Plus, Edit, Trash2, User, Camera } from 'lucide-react';
import { useOrganization } from '../hooks/useOrganization';

interface Entity {
  id: string;
  created_at: string;
  name: string;
  description: string | null;
  profile_picture: string | null;
  organization_id: string;
  additional_info: any;
}

interface EntityFormData {
  name: string;
  description: string;
  additional_info: string;
}

const Profiles: React.FC = () => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [formData, setFormData] = useState<EntityFormData>({
    name: '',
    description: '',
    additional_info: ''
  });
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isOfficialOrganization, loading: orgLoading } = useOrganization();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3030';

  useEffect(() => {
    if (!orgLoading && isOfficialOrganization) {
      fetchEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgLoading, isOfficialOrganization]);

  const getTokenFromCookies = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sgo_access_token') {
        return value;
      }
    }
    return null;
  };

  const decodeJWT = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  };

  const fetchEntities = async () => {
    console.log('Fetching entities...');
    try {
      const token = getTokenFromCookies();
      console.log('Token for fetch:', token ? 'exists' : 'not found');

      if (!token) {
        console.log('No token found, skipping fetch');
        return;
      }

      console.log('Making request to:', `${API_BASE_URL}/entities`);

      const response = await fetch(`${API_BASE_URL}/entities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Entities data:', data);
        setEntities(data.data || []);
      } else {
        const errorData = await response.text();
        console.error('Fetch error response:', errorData);
      }
    } catch (error) {
      console.error('Erro ao buscar entidades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setProfilePicture(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting form...');

    try {
      const token = getTokenFromCookies();
      console.log('Token:', token ? 'exists' : 'not found');

      if (!token) {
        alert('Token de autenticação não encontrado. Faça login novamente.');
        return;
      }

      // Decode token to check user data
      const decodedToken = decodeJWT(token);
      console.log('Decoded token:', decodedToken);
      console.log('User metadata:', decodedToken?.user_metadata);

      // Use FormData for both create and edit
      const formDataPayload = new FormData();
      formDataPayload.append('name', formData.name);
      if (formData.description) {
        formDataPayload.append('description', formData.description);
      }
      if (formData.additional_info) {
        try {
          JSON.parse(formData.additional_info); // Validate JSON
          formDataPayload.append('additional_info', formData.additional_info);
        } catch (jsonError) {
          alert('Formato JSON inválido nas informações adicionais');
          return;
        }
      }
      if (profilePicture) {
        formDataPayload.append('profile_picture', profilePicture);
      }

      let response;
      const url = editingEntity
        ? `${API_BASE_URL}/entities/${editingEntity.id}`
        : `${API_BASE_URL}/entities`;

      console.log('Making request to:', url);

      response = await fetch(url, {
        method: editingEntity ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataPayload
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Response data:', result);

        // No need to upload profile picture separately since it's included in FormData

        await fetchEntities();
        handleCloseModal();
        alert('Perfil salvo com sucesso!');
      } else {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        alert(`Erro ao salvar perfil: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Erro ao salvar entidade:', error);
      alert(`Erro ao salvar perfil: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const handleDelete = async (entityId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta entidade?')) return;

    try {
      const token = getTokenFromCookies();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/entities/${entityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchEntities();
      }
    } catch (error) {
      console.error('Erro ao excluir entidade:', error);
    }
  };

  const handleEdit = (entity: Entity) => {
    setEditingEntity(entity);
    setFormData({
      name: entity.name,
      description: entity.description || '',
      additional_info: entity.additional_info ? JSON.stringify(entity.additional_info, null, 2) : ''
    });

    // Set current profile picture as preview
    if (entity.profile_picture) {
      setProfilePicturePreview(entity.profile_picture);
    } else {
      setProfilePicturePreview('');
    }
    setProfilePicture(null);

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntity(null);
    setFormData({
      name: '',
      description: '',
      additional_info: ''
    });
    setProfilePicture(null);
    setProfilePicturePreview('');
  };

  // Show loading while checking organization
  if (orgLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Redirect if not official organization
  if (!isOfficialOrganization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600">Esta funcionalidade está disponível apenas para organizações oficiais.</p>
          <button
            onClick={() => window.location.href = '/feed'}
            className="mt-4 px-4 py-2 bg-brown-600 hover:bg-brown-700 text-white rounded-lg transition-colors"
          >
            Voltar ao Feed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        currentPath="/profiles"
        onToggle={setSidebarExpanded}
      />

      <div className={`flex-1 pt-16 md:pt-8 py-8 transition-all duration-300 ml-0 ${
        sidebarExpanded ? 'md:ml-64' : 'md:ml-16'
      }`}>
        <section className="w-full bg-white p-6 rounded-sm mx-4">
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-semibold text-brown-800 text-gradient">Perfis</h2>
              <p className="text-gray-600 mt-2 text-brown-600">Gerencie as entidades da sua organização</p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center space-x-2 bg-brown-600 hover:bg-brown-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Novo Perfil</span>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Carregando...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entities.map((entity) => (
                <div key={entity.id} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    {entity.profile_picture ? (
                      <img
                        src={entity.profile_picture}
                        alt={entity.name}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          // Fallback to default avatar if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center ${entity.profile_picture ? 'hidden' : ''}`}>
                      <User className="w-6 h-6 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{entity.name}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(entity.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>

                  {entity.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{entity.description}</p>
                  )}

                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleEdit(entity)}
                      className="p-2 text-gray-500 hover:text-brown-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(entity.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {entities.length === 0 && (
                <div className="col-span-full text-center py-8">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma entidade encontrada</p>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="mt-2 text-brown-600 hover:underline"
                  >
                    Criar primeira entidade
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingEntity ? 'Editar Perfil' : 'Novo Perfil'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foto de Perfil
                </label>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {profilePicturePreview ? (
                      <img
                        src={profilePicturePreview}
                        alt="Preview"
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center border-2 border-gray-300">
                        <User className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute -bottom-1 -right-1 bg-brown-600 hover:bg-brown-700 text-white rounded-full p-1.5 transition-colors"
                    >
                      <Camera className="w-3 h-3" />
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      Clique no ícone para escolher uma foto
                    </p>
                    <p className="text-xs text-gray-500">
                      JPG, PNG ou GIF até 5MB
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informações Adicionais (JSON)
                </label>
                <textarea
                  rows={4}
                  value={formData.additional_info}
                  onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                  placeholder='{"campo": "valor"}'
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brown-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brown-600 hover:bg-brown-700 text-white rounded-lg transition-colors"
                >
                  {editingEntity ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profiles;